import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, AlertCircle, Plus, Check, Loader2, Search, ChevronDown } from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { PROFILE_STATUSES } from '../../lib/constants';
import { useLanguage } from '../../contexts/LanguageContext';
import { citiesService, sourcesService } from '../../services/api';
import { DatePicker, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem } from '../ui';

export function SearchableCitySelect({ value, onChange, cities, onCreateCity, loading, onToggleOpen }) {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newCityInput, setNewCityInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const toggleDropdown = (openState) => {
    setIsOpen(openState);
    if (onToggleOpen) onToggleOpen(openState);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        toggleDropdown(false);
        setIsAddingNew(false);
        setErrorMsg('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current && !isAddingNew) {
      searchInputRef.current.focus();
    }
  }, [isOpen, isAddingNew]);

  const filteredCities = useMemo(() => {
    if (!searchTerm.trim()) return cities;
    return cities.filter((c) =>
      c.name.toLowerCase().includes(searchTerm.toLowerCase().trim())
    );
  }, [cities, searchTerm]);

  const handleSaveCity = async () => {
    const nameToSave = newCityInput.trim();
    if (!nameToSave) {
      setErrorMsg(t('cityNameRequired') || 'City name is required');
      return;
    }
    setIsSaving(true);
    setErrorMsg('');
    try {
      await onCreateCity(nameToSave);
      setIsAddingNew(false);
      setNewCityInput('');
      toggleDropdown(false);
      setSearchTerm('');
    } catch (err) {
      setErrorMsg(err.message || t('failedToSaveCity') || 'Failed to save city');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="relative mt-1" ref={containerRef}>
      {/* Trigger Input/Button */}
      <button
        type="button"
        onClick={() => {
          const next = !isOpen;
          toggleDropdown(next);
          setIsAddingNew(false);
          setErrorMsg('');
        }}
        className="w-full p-2 min-h-[38px] border border-gray-200 rounded-lg outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-white text-left flex items-center justify-between text-sm"
      >
        <span className={value ? 'text-gray-800 font-medium' : 'text-gray-400'}>
          {value || t('selectCity') || '-- Select City --'}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
      </button>

      {/* Floating Compact Dropdown Popover */}
      {isOpen && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl z-[150] overflow-hidden flex flex-col animate-fade-in max-h-64">
          {!isAddingNew ? (
            <>
              {/* Sticky Top Search Bar */}
              <div className="p-2 border-b bg-gray-50 flex items-center gap-2 shrink-0">
                <Search className="w-4 h-4 text-gray-400 shrink-0" />
                <input
                  ref={searchInputRef}
                  type="text"
                  className="w-full text-xs outline-none bg-transparent placeholder-gray-400"
                  placeholder={t('searchCity') || 'Search city...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="p-0.5 text-gray-400 hover:text-gray-600 rounded-full"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Scrollable Cities List */}
              <div className="overflow-y-auto max-h-40 flex-1 divide-y divide-gray-50 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent">
                {loading ? (
                  <div className="p-3 text-center text-xs text-gray-400 flex items-center justify-center gap-1.5">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('loadingCities') || 'Loading cities...'}
                  </div>
                ) : filteredCities.length > 0 ? (
                  filteredCities.map((c) => {
                    const isSelected = value?.toLowerCase() === c.name.toLowerCase();
                    return (
                      <button
                        key={c.id || c.name}
                        type="button"
                        onClick={() => {
                          onChange(c.name);
                          toggleDropdown(false);
                          setSearchTerm('');
                        }}
                        className={`w-full text-left px-3 py-2 text-xs font-medium transition-colors flex items-center justify-between ${
                          isSelected
                            ? 'bg-blue-50 text-blue-700 font-bold'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <span>{c.name}</span>
                        {isSelected && <Check className="w-3.5 h-3.5 text-blue-600 shrink-0" />}
                      </button>
                    );
                  })
                ) : (
                  <div className="p-3 text-center text-xs text-gray-500">
                    {t('noCityFound') ? t('noCityFound').replace('{searchTerm}', searchTerm) : `No city found for "${searchTerm}"`}
                  </div>
                )}
              </div>

              {/* Bottom Sticky "+ Add New City" Button */}
              <div className="border-t bg-gray-50 p-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingNew(true);
                    setNewCityInput(searchTerm);
                    setErrorMsg('');
                  }}
                  className="w-full text-left px-2.5 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 font-bold text-xs flex items-center gap-1.5 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5 shrink-0" />
                  <span className="truncate">
                    {searchTerm.trim() ? (t('addAsNewCity') ? t('addAsNewCity').replace('{searchTerm}', searchTerm.trim()) : `Add "${searchTerm.trim()}" as new city`) : (t('addNewCityTitle') || 'Add New City')}
                  </span>
                </button>
              </div>
            </>
          ) : (
            /* Inline City Creation Form */
            <div className="p-3 bg-blue-50/40 space-y-2 shrink-0">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold text-gray-800">{t('addNewCityTitle') || 'Add New City'}</p>
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingNew(false);
                    setErrorMsg('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              <input
                type="text"
                className="w-full p-2 border border-blue-300 rounded-lg text-xs outline-none bg-white focus:ring-2 focus:ring-blue-100"
                placeholder={t('enterCityName') || 'Enter city name...'}
                value={newCityInput}
                onChange={(e) => {
                  setNewCityInput(e.target.value);
                  setErrorMsg('');
                }}
                autoFocus
              />
              {errorMsg && (
                <p className="text-red-500 text-[11px] font-semibold">{errorMsg}</p>
              )}
              <div className="flex justify-end gap-1.5 pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingNew(false);
                    setErrorMsg('');
                  }}
                  className="px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-xs font-semibold"
                >
                  {t('cancel') || 'Cancel'}
                </button>
                <button
                  type="button"
                  onClick={handleSaveCity}
                  disabled={isSaving}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-xs font-bold flex items-center gap-1 transition-colors"
                >
                  {isSaving ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Check className="w-3.5 h-3.5" />
                  )}
                  {t('saveCity') || 'Save City'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProfileModal({ onClose, onSave, initialData }) {
  const { t } = useLanguage();

  const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState({
    clientName: initialData?.clientName || '',
    contactPersonName: initialData?.contactPersonName || '',
    designation: initialData?.designation || '',
    contactDetailExt: initialData?.contactDetailExt || '',
    email: initialData?.email || '',
    mobile: initialData?.mobile || '',
    address: initialData?.address || '',
    city: initialData?.city || '',
    projectName: initialData?.projectName || '',
    sourceId: initialData?.sourceId || '',
    status: initialData?.status || 'New',
    date: initialData?.date ? new Date(initialData.date).toISOString().split('T')[0] : getTodayDateString()
  });

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Cities State & Dropdown Open Tracking
  const [cities, setCities] = useState([]);
  const [sources, setSources] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);

  const formBodyRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    setLoadingCities(true);
    citiesService.getAll({ activeOnly: 'true' })
      .then((res) => {
        if (isMounted) {
          const list = Array.isArray(res) ? res : [];
          setCities(list);
        }
      })
      .catch((err) => console.error('Error fetching cities for modal:', err))
      .finally(() => {
        if (isMounted) setLoadingCities(false);
      });

    sourcesService.getAll()
      .then((res) => {
        if (isMounted) setSources(res);
      })
      .catch((err) => console.error('Error fetching sources for modal:', err));

    return () => { isMounted = false; };
  }, []);

  const handleCreateNewCity = async (cityName) => {
    const created = await citiesService.create({ name: cityName });
    if (created) {
      setCities((prev) => [...prev, created]);
      handleInputChange('city', created.name);
    } else {
      handleInputChange('city', cityName);
    }
  };

  const validate = () => {
    const newErrors = {};

    // 1. Client Name (Required)
    if (!formData.clientName || !formData.clientName.trim()) {
      newErrors.clientName = t('clientNameRequired') || 'Client Name is required.';
    }

    // 10. Date (Required)
    if (!formData.date || !formData.date.trim()) {
      newErrors.date = t('dateRequired') || 'Date is required.';
    }

    // 5. Email validation (Optional, but must be valid if entered)
    if (formData.email && formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        newErrors.email = t('invalidEmail') || 'Invalid email address format';
      }
    }

    // 6. Mobile validation (Optional)
    if (formData.mobile && formData.mobile.trim()) {
      const digits = formData.mobile.replace(/\D/g, '');
      if (digits.length > 0 && digits.length < 7) {
        newErrors.mobile = t('invalidPhone') || 'Invalid mobile number format.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async () => {
    if (validate()) {
      setIsSubmitting(true);
      setApiError('');
      try {
        const payload = {
          clientName: formData.clientName.trim(),
          contactPersonName: formData.contactPersonName ? formData.contactPersonName.trim() : '',
          designation: formData.designation ? formData.designation.trim() : '',
          contactDetailExt: formData.contactDetailExt ? formData.contactDetailExt.trim() : '',
          email: formData.email ? formData.email.trim() : '',
          mobile: formData.mobile ? formData.mobile.trim() : '',
          address: formData.address ? formData.address.trim() : '',
          city: formData.city ? formData.city.trim() : '',
          projectName: formData.projectName ? formData.projectName.trim() : '',
          sourceId: formData.sourceId ? parseInt(formData.sourceId) : null,
          date: formData.date
        };
        if (initialData) {
          await onSave(initialData.id, payload);
        } else {
          await onSave(payload);
        }
        
        if (onClose) {
          onClose();
        }
      } catch (err) {
        setApiError(err.message || 'Failed to save profile.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCityToggleOpen = (isOpen) => {
    setIsCityDropdownOpen(isOpen);
    if (isOpen && formBodyRef.current) {
      setTimeout(() => {
        formBodyRef.current?.scrollTo({
          top: formBodyRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl animate-scale-in flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center rounded-t-2xl shrink-0">
          <h3 className="font-bold text-gray-800 text-lg">
            {initialData ? t('editProfile') || 'Edit Profile' : t('addNewProfile') || 'Add New Profile'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* API Error Alert */}
        {apiError && (
          <div className="mx-6 mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl animate-fade-in shrink-0">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{apiError}</span>
            <button onClick={() => setApiError('')} className="ml-auto text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Rejection Notice if editing rejected profile */}
        {initialData?.approvalStatus === 'REJECTED' && (
          <div className="mx-6 mt-4 p-3.5 bg-rose-50 border border-rose-200 text-rose-800 rounded-xl text-xs flex items-start gap-2.5 shrink-0 animate-fade-in">
            <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
            <div>
              <span className="font-bold block">Profile was rejected by approver</span>
              {initialData.approvalRemarks && (
                <p className="mt-0.5 text-rose-700 font-medium">"{initialData.approvalRemarks}"</p>
              )}
              <span className="text-gray-500 block mt-1">Make required corrections and save to resubmit for approval.</span>
            </div>
          </div>
        )}


        {/* Horizontal 2-Column Grid Form Body with Dynamic Expandable Padding */}
        <div ref={formBodyRef} className={`p-6 overflow-y-auto transition-all duration-300 ${isCityDropdownOpen ? 'pb-48' : 'pb-6'}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* 1. Client Name * */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase">
                {t('clientName') || 'Client Name'} <span className="text-red-500 ml-0.5">*</span>
              </label>
              <input
                className={`w-full p-2 border rounded-lg mt-1 outline-none transition-all focus:ring-2 ${
                  errors.clientName
                    ? 'border-red-400 bg-red-50/30 focus:ring-red-200 focus:border-red-500'
                    : 'border-gray-200 focus:ring-blue-100 focus:border-blue-500'
                }`}
                value={formData.clientName}
                onChange={(e) => handleInputChange('clientName', e.target.value)}
                placeholder={t('enterClientName') || 'Enter Client Name'}
              />
              {errors.clientName && (
                <p className="text-red-500 text-[11px] font-semibold mt-1 animate-fade-in">{errors.clientName}</p>
              )}
            </div>

            {/* 2. Profile Person Name */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase">
                {t('contactPersonName') || 'Profile Person Name'}
              </label>
              <input
                className="w-full p-2 border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                value={formData.contactPersonName}
                onChange={(e) => handleInputChange('contactPersonName', e.target.value)}
                placeholder={t('enterContactName') || 'Enter Contact Person Name'}
              />
            </div>

            {/* 3. Designation */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase">
                {t('designation') || 'Designation'}
              </label>
              <input
                className="w-full p-2 border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                value={formData.designation}
                onChange={(e) => handleInputChange('designation', e.target.value)}
                placeholder={t('enterDesignation') || 'Enter Designation'}
              />
            </div>

            {/* 4. Profile Detail / Extension */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase">
                {t('contactDetailExt') || 'Profile Detail / Extension'}
              </label>
              <input
                className="w-full p-2 border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                value={formData.contactDetailExt}
                onChange={(e) => handleInputChange('contactDetailExt', e.target.value)}
                placeholder={t('enterExtension') || 'Enter Detail / Extension'}
              />
            </div>

            {/* 5. Email */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase">
                {t('email') || 'Email'}
              </label>
              <input
                className={`w-full p-2 border rounded-lg mt-1 outline-none transition-all focus:ring-2 ${
                  errors.email
                    ? 'border-red-400 bg-red-50/30 focus:ring-red-200 focus:border-red-500'
                    : 'border-gray-200 focus:ring-blue-100 focus:border-blue-500'
                }`}
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder={t('enterEmail') || 'Enter Email'}
              />
              {errors.email && (
                <p className="text-red-500 text-[11px] font-semibold mt-1 animate-fade-in">{errors.email}</p>
              )}
            </div>

            {/* 6. Mobile */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase">
                {t('mobile') || 'Mobile'}
              </label>
              <PhoneInput
                country={'pk'}
                value={formData.mobile}
                onChange={(phone) => handleInputChange('mobile', phone)}
                containerClass="mt-1"
                inputClass={`!w-full !p-2 !pl-[48px] !border !rounded-lg !outline-none !transition-all focus:!ring-2 ${
                  errors.mobile
                    ? '!border-red-400 !bg-red-50/30 focus:!ring-red-200 focus:!border-red-500'
                    : '!border-gray-200 focus:!ring-blue-100 focus:!border-blue-500'
                }`}
                buttonClass={`!border-gray-200 !bg-gray-50 !rounded-l-lg ${errors.mobile ? '!border-red-400 !bg-red-50/30' : ''}`}
                dropdownClass="!rounded-lg !shadow-2xl !border-gray-200 !max-h-[140px] !overflow-x-hidden [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent"
                inputProps={{ placeholder: t('enterMobile') || 'Enter Mobile' }}
              />
              {errors.mobile && (
                <p className="text-red-500 text-[11px] font-semibold mt-1 animate-fade-in">{errors.mobile}</p>
              )}
            </div>

            {/* 7. Address */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase">
                {t('address') || 'Address'}
              </label>
              <input
                className="w-full p-2 border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder={t('enterAddress') || 'Enter Address'}
              />
            </div>

            {/* 8. City (Custom Searchable Dropdown with Sticky Search & Add City) */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase">
                {t('city') || 'City'}
              </label>
              <SearchableCitySelect
                value={formData.city}
                onChange={(val) => handleInputChange('city', val)}
                cities={cities}
                onCreateCity={handleCreateNewCity}
                loading={loadingCities}
                onToggleOpen={handleCityToggleOpen}
              />
            </div>

            {/* 9. Project Name */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase">
                {t('projectName') || 'Project Name'}
              </label>
              <input
                className="w-full p-2 border border-gray-200 rounded-lg mt-1 outline-none transition-all focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                value={formData.projectName}
                onChange={(e) => handleInputChange('projectName', e.target.value)}
                placeholder={t('enterProjectName') || 'Enter Project Name'}
              />
            </div>

            {/* Source */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase">
                {t('source') || 'Source'}
              </label>
              <Dropdown>
                <DropdownTrigger>
                  <button type="button" className="w-full p-2 border border-gray-200 bg-white rounded-lg mt-1 text-left flex justify-between items-center text-sm min-h-[40px] focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all outline-none">
                    {sources.find(s => String(s.id) === String(formData.sourceId))?.name || t('selectSource') || 'Select Source'}
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </button>
                </DropdownTrigger>
                <DropdownPopover className="w-full z-[150]">
                  <DropdownMenu
                    selectedKeys={new Set([String(formData.sourceId)])}
                    selectionMode="single"
                    onSelectionChange={(keys) => handleInputChange('sourceId', Array.from(keys)[0])}
                  >
                    {sources.length ? sources.map((s) => (
                      <DropdownItem key={s.id} id={String(s.id)}>
                        {s.name}
                      </DropdownItem>
                    )) : <DropdownItem id="">{t('noSourcesAvailable') || 'No Sources'}</DropdownItem>}
                  </DropdownMenu>
                </DropdownPopover>
              </Dropdown>
            </div>

            {/* Status */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase">
                {t('status') || 'Status'}
              </label>
              <Dropdown>
                <DropdownTrigger>
                  <button type="button" className="w-full p-2 border border-gray-200 bg-white rounded-lg mt-1 text-left flex justify-between items-center text-sm min-h-[40px] focus:ring-2 focus:ring-blue-100 focus:border-blue-500 transition-all outline-none">
                    {formData.status || t('selectStatus') || 'Select Status'}
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </button>
                </DropdownTrigger>
                <DropdownPopover className="w-full z-[150]">
                  <DropdownMenu
                    selectedKeys={new Set([formData.status])}
                    selectionMode="single"
                    onSelectionChange={(keys) => handleInputChange('status', Array.from(keys)[0])}
                  >
                    {PROFILE_STATUSES.map((s) => (
                      <DropdownItem key={s} id={s}>
                        {s}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </DropdownPopover>
              </Dropdown>
            </div>

            {/* 10. Date * */}
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase">
                {t('date') || 'Date'} <span className="text-red-500 ml-0.5">*</span>
              </label>
              <DatePicker
                name="date"
                value={formData.date}
                onChange={(e) => handleInputChange('date', e.target.value)}
                error={!!errors.date}
              />
              {errors.date && (
                <p className="text-red-500 text-[11px] font-semibold mt-1 animate-fade-in">{errors.date}</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t flex justify-end gap-2 rounded-b-2xl shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-500 hover:bg-gray-100 rounded-lg font-medium transition-colors"
          >
            {t('cancel') || 'Cancel'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`px-6 py-2 rounded-lg font-bold transition-colors shadow-sm ${
              isSubmitting ? 'bg-blue-400 text-white cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {isSubmitting ? t('saving') || 'Saving...' : t('saveProfile') || 'Save Profile'}
          </button>
        </div>
      </div>
    </div>
  );
}
